import NavLink from '@/components/NavLink';
import { MEDIA_URL } from '@/constants/env';
import { fadeInDown } from '@/constants/motions';
import { primaryMenu } from '@/content';
import Image from '@/ui/Image';
import dismissFilled from '@iconify/icons-fluent/dismiss-24-filled';
import listFilled from '@iconify/icons-fluent/list-24-filled';
import { Icon } from '@iconify/react/dist/offline';
import cx from 'classnames';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { useOnWindowScroll as useOnWindowScrollOriginal, useOutsideClick } from 'rooks';

export default function HeaderBlock({ navBar }) {
  const [isOpen, setOpen] = useState(false);
  // Close nav when clicking outside
  const headerRef = useRef();
  useOutsideClick(headerRef, () => setOpen(false));

  // Detect if window is scrolled to show shadow under nav
  const useOnWindowScroll = typeof window === 'object' ? useOnWindowScrollOriginal : () => {};
  const [sticky, setSticky] = useState(false);
  useOnWindowScroll(() => {
    if (window.scrollY > 0) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  });

  return (
    <motion.header
      ref={headerRef}
      className={cx('fixed top-0 z-40 h-20 w-full bg-white lg:h-28', {
        'shadow-lg': sticky,
      })}
      initial="hidden"
      transition={fadeInDown.transition}
      variants={fadeInDown}
      viewport={fadeInDown.viewport}
      whileInView="visible"
    >
      <div className="container-screen h-full">
        <div className="flex h-full flex-col justify-between lg:flex-row">
          <div className="relative z-10 flex h-full shrink-0 items-center justify-between bg-white px-4 xl:px-0">
            <Link href="/">
              <a className="focus-visible:outline-dashed focus-visible:outline-primary-200">
                <Image
                  alt="Logo"
                  className="h-16 w-24 object-contain lg:h-24 lg:w-32"
                  src={MEDIA_URL.concat(navBar?.companyLogo?.data?.attributes?.url || '')}
                />
              </a>
            </Link>
            <div className="flex items-center gap-4">
              <Image
                alt="Logo"
                className="h-14 w-24 object-contain lg:hidden"
                src={MEDIA_URL.concat(navBar?.otherLogo?.data?.attributes?.url || '')}
              />

              <MobileNavBarToggleButton isOpen={isOpen} setOpen={setOpen} />
            </div>
          </div>
          <DesktopNavBar
            dnvLogo={navBar?.otherLogo?.data?.attributes?.url || ''}
            primaryMenu={navBar?.primaryMenu || []}
          />
          <MobileNavBar isOpen={isOpen} />
        </div>
      </div>
    </motion.header>
  );
}

function DesktopNavBar({ primaryMenu, dnvLogo }) {
  return (
    <nav className="hidden lg:flex lg:items-center lg:gap-4">
      {(primaryMenu || []).map(({ exact, ...props }, i) => (
        <NavLink {...props} key={i} exact={exact} />
      ))}
      <Image
        alt="Logo"
        className="h-14 w-24 object-contain"
        src={MEDIA_URL.concat(dnvLogo || '')}
      />
    </nav>
  );
}

function MobileNavBarToggleButton({ isOpen, setOpen }) {
  return (
    <button
      className="flex h-12 w-12 items-center justify-center rounded-md text-primary-500 transition hover:bg-primary-500/10 focus:outline-none focus-visible:bg-primary-500/20 lg:hidden"
      type="button"
      onClick={() => setOpen(!isOpen)}
    >
      <motion.span layout transition={{ duration: 0.3 }}>
        {isOpen ? (
          <Icon height="28" icon={dismissFilled} />
        ) : (
          <Icon height="32" icon={listFilled} />
        )}
      </motion.span>
    </button>
  );
}

function MobileNavBar({ isOpen }) {
  return (
    <motion.nav
      animate={isOpen ? 'open' : 'closed'}
      className="rounded-b-lg bg-white px-4 pb-6 shadow-lg lg:hidden"
      initial={false}
      variants={{
        closed: {
          y: '-100%',
          transition: {
            ease: 'easeIn',
            staggerChildren: 0.05,
            staggerDirection: -1,
          },
        },
        open: {
          y: 0,
          transition: {
            ease: 'easeOut',
            staggerChildren: 0.07,
            delayChildren: 0.2,
          },
        },
      }}
    >
      {(primaryMenu || []).map(({ exact, ...props }, i) => (
        <NavLink
          {...props}
          key={i}
          exact={exact}
          variants={{
            closed: {
              y: 50,
              opacity: 0,
              transition: {
                y: { stiffness: 1000 },
              },
            },
            open: {
              y: 0,
              opacity: 1,
              transition: {
                y: { stiffness: 1000, velocity: -100 },
              },
            },
          }}
        />
      ))}
    </motion.nav>
  );
}
